import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { serverStatusQueryOptions } from "@/lib/queries";
import { useServerStatusStore } from "@/hooks/serverStatusStore";
import {
  recordConnectivityFailure,
  recordConnectivitySuccess,
} from "@/hooks/connectivityTracker";

type ServerStatus = "online" | "offline";

/**
 * Syncs confirmed server connectivity into `serverStatusStore`.
 * Health poll outcomes share the same failure counter as transport errors
 * (`recordConnectivityFailure` in the global query handler).
 */
export function useServerStatus(): ServerStatus {
  const { data, isError, errorUpdatedAt, isSuccess } = useQuery(
    serverStatusQueryOptions(),
  );
  const storeStatus = useServerStatusStore((s) => s.status);

  const lastErrorUpdatedAtRef = useRef(0);

  useEffect(() => {
    if (isSuccess && data) {
      lastErrorUpdatedAtRef.current = 0;
      recordConnectivitySuccess();
      return;
    }

    if (!isError) return;
    if (errorUpdatedAt === lastErrorUpdatedAtRef.current) return;

    lastErrorUpdatedAtRef.current = errorUpdatedAt;
    recordConnectivityFailure();
  }, [isSuccess, data, isError, errorUpdatedAt]);

  return storeStatus;
}
